import { LitElement, html, css, nothing } from 'lit';
import { contactLinkStyles } from './contact-prompt.js';
import { customElement, property, state } from 'lit/decorators.js';
import { themeTokens, resetStyles, baseStyles } from '../../styles/tokens.css.js';
import { formatTime, todayInTimezone } from '../../utils/timezone.js';
import type { AvailableSlot } from '../../api/types.js';
import type { WorkingHoursSchedule } from '@yebo/shared';
import type { AvailabilityAlternateTime, AvailabilityAlternateSection } from '../../api/client.js';
import {
  availabilityReasonHeadline,
  availabilityReasonIsDeadEnd,
  NO_AVAILABILITY_DEFAULT,
} from './availability-reason-copy.js';

// Side-effect import: registers <yebo-step-datetime-recovery> (R10.4 empty state).
import './step-datetime-recovery.js';
// #953 — also registers <yebo-ticket-coupon> for the recurring occurrence picker.
import {
  COMPED_TOTAL_COPY,
  couponStatusStyles,
  isCompedQuote,
  renderCouponStatusContent,
} from './ticket-coupon.js';
import { tieredTicketTotalLabel, type TicketTotalLine } from './ticket-total-label.js';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

@customElement('yebo-step-datetime')
export class YeboStepDatetime extends LitElement {
  static styles = [
    themeTokens,
    resetStyles,
    baseStyles,
    contactLinkStyles, // #592 / Round-16 A2
    couponStatusStyles, // #954 a11y M1 — the recurring coupon live region
    css`
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
      }

      .step-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: var(--yebo-space-md);
      }

      /* Calendar grid */
      .cal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--yebo-space-xs);
      }
      .cal-title {
        font-weight: 600;
        font-size: var(--yebo-font-size-base);
        color: var(--yebo-text-heading);
      }
      .cal-nav {
        background: none;
        border: 1px solid var(--yebo-border);
        border-radius: var(--yebo-radius-sm);
        padding: 4px 10px;
        cursor: pointer;
        font-size: var(--yebo-font-size-base);
        color: var(--yebo-text);
      }
      .cal-nav:hover:not(:disabled) {
        background: var(--yebo-bg-elevated);
        border-color: var(--yebo-primary);
      }
      .cal-nav:disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }
      .cal-grid {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        gap: 2px;
        text-align: center;
      }
      .cal-weekday {
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-text-muted);
        padding: var(--yebo-space-xs);
        font-weight: 500;
      }
      .cal-day {
        padding: 6px;
        border-radius: var(--yebo-radius-sm);
        cursor: pointer;
        font-size: var(--yebo-font-size-sm);
        border: none;
        background: none;
        color: var(--yebo-text);
        transition: background var(--yebo-transition);
      }
      .cal-day:hover:not(.disabled):not(.selected) {
        background: var(--yebo-bg-elevated);
      }
      .cal-day.selected {
        background: var(--yebo-primary);
        color: var(--yebo-bg);
        font-weight: 600;
      }
      .cal-day.today {
        font-weight: 700;
        color: var(--yebo-primary);
      }
      .cal-day.today.selected {
        color: var(--yebo-bg);
      }
      .cal-day.disabled {
        color: var(--yebo-border);
        cursor: not-allowed;
      }
      .cal-day.empty {
        cursor: default;
      }
      @media (pointer: coarse) {
        .cal-day {
          min-width: 44px;
          min-height: 44px;
          /* square cells so the selected pill reads as a neat circle */
          aspect-ratio: 1;
          border-radius: 50%;
        }
        .cal-nav {
          min-height: 44px;
          padding: 4px 14px;
        }
      }

      /* Time slots */
      .slots-label {
        font-weight: 500;
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-text-muted);
        margin-bottom: var(--yebo-space-xs);
      }
      /*
       * The empty state is an OUTCOME the guest must read and act on, not a heading
       * over a list. .slots-label is deliberately muted and small because it labels
       * the time grid ("Available times"); reusing it here rendered a full sentence —
       * sometimes the only thing on screen — as de-emphasised fine print.
       * (No backticks in here: this block sits inside a css tagged template.)
       */
      .empty-state {
        font-weight: 500;
        font-size: var(--yebo-font-size-base);
        color: var(--yebo-text);
        line-height: 1.5;
        margin: var(--yebo-space-sm) 0 0;
        max-width: 46ch;
      }
      .empty-state-hint {
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-text-muted);
        margin: var(--yebo-space-xs) 0 0;
        max-width: 46ch;
      }
      .empty-state-cta {
        display: inline-flex;
        align-items: baseline;
        gap: 0.4em;
        flex-wrap: wrap;
        margin-top: var(--yebo-space-xs);
        font-size: var(--yebo-font-size-sm);
        /* Round-16 A2: was --yebo-primary, i.e. the VENUE'S brand colour — blue
           at Lekker and near-black green at Creation, both hard to read on the
           dark panel. Body text colour is legible by construction; the
           underline carries the affordance colour used to. */
        color: var(--yebo-text);
        text-decoration: underline;
        text-decoration-thickness: 1px;
        text-underline-offset: 2px;
        font-weight: 600;
      }
      .empty-state-cta:hover,
      .empty-state-cta:focus-visible {
        color: var(--yebo-primary);
        text-decoration-thickness: 2px;
      }
      .empty-state-cta .num {
        color: var(--yebo-text-muted);
        font-weight: 500;
      }
      .slots-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
        gap: var(--yebo-space-xs);
      }
      .slot-btn {
        padding: 6px;
        border: 1px solid var(--yebo-border);
        border-radius: var(--yebo-radius);
        background: var(--yebo-bg-elevated);
        cursor: pointer;
        font-size: var(--yebo-font-size-sm);
        font-family: var(--yebo-font);
        color: var(--yebo-text);
        text-align: center;
        transition: all var(--yebo-transition);
      }
      .slot-btn:hover:not(.unavailable):not(.selected) {
        border-color: var(--yebo-primary);
        background: var(--yebo-primary-light);
      }
      .slot-btn.selected {
        background: var(--yebo-primary);
        color: var(--yebo-bg);
        border-color: var(--yebo-primary);
        font-weight: 600;
      }
      .slot-btn.unavailable {
        opacity: 0.3;
        cursor: not-allowed;
      }

      .tz-label {
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-text-muted);
        text-align: center;
        margin-top: var(--yebo-space-xs);
      }

      .actions {
        display: flex;
        justify-content: space-between;
        padding-top: var(--yebo-space-md);
      }

      /* #130 — recurring occurrence picker */
      .occ-list {
        display: flex;
        flex-direction: column;
        gap: var(--yebo-space-sm);
      }
      .occ-option {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--yebo-space-md);
        text-align: left;
        width: 100%;
        background: var(--yebo-bg-elevated);
        border: 1px solid var(--yebo-border);
        border-radius: var(--yebo-radius);
        padding: 12px 16px;
        cursor: pointer;
        transition: border-color var(--yebo-transition), background var(--yebo-transition);
      }
      .occ-option:hover:not([aria-disabled='true']) {
        border-color: var(--yebo-primary);
      }
      .occ-option[aria-pressed='true'] {
        border-color: var(--yebo-primary);
        background: var(--yebo-primary-light);
      }
      .occ-option[aria-disabled='true'] {
        opacity: 0.55;
        cursor: not-allowed;
      }
      .occ-date {
        font-size: var(--yebo-font-size-base);
        font-weight: 600;
        color: var(--yebo-text);
      }
      .occ-remaining {
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-text-muted);
        white-space: nowrap;
      }
      .occ-remaining.soldout {
        color: var(--yebo-error);
      }
      .occ-empty {
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-text-muted);
      }
      .occ-total {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: var(--yebo-space-md);
        background: var(--yebo-bg-elevated);
        border: 1px solid var(--yebo-border);
        border-radius: var(--yebo-radius);
        padding: 12px 16px;
        margin-top: var(--yebo-space-md);
      }
      .occ-total-label {
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-text-muted);
      }
      .occ-total-value {
        font-size: var(--yebo-font-size-lg);
        font-weight: 700;
        color: var(--yebo-text-heading);
      }
      /* #953 — the recurring promo field sits under the occurrence list. */
      yebo-ticket-coupon {
        margin-top: var(--yebo-space-md);
      }
      .occ-error {
        color: var(--yebo-error);
        font-size: var(--yebo-font-size-sm);
        margin-top: var(--yebo-space-sm);
      }
    `,
  ];

  @property({ type: String }) timezone = 'UTC';
  @property({ type: String, attribute: 'selected-date' }) selectedDate: string | null = null;
  @property({ type: String, attribute: 'selected-slot' }) selectedSlot: string | null = null;
  @property({ type: Array }) slots: AvailableSlot[] = [];
  @property({ type: Boolean }) loading = false;

  // #130 — recurring "standard experience" occurrence picker. When `recurring`
  // is set, the step swaps its calendar+slots for a list of the series' bookable
  // occurrences (date + remaining covers), loaded lazily by the host. Picking one
  // resolves the opaque occId (sent verbatim to quote/checkout/book). One-off and
  // ordinary bookings leave `recurring` false and keep the calendar unchanged.
  @property({ type: Boolean }) recurring = false;
  @property({ type: Array }) occurrences: Array<{ occId: string; date: string; coversRemaining: number; soldOut?: boolean }> = [];
  @property({ type: Boolean, attribute: 'occurrences-loading' }) occurrencesLoading = false;
  @property({ type: String, attribute: 'selected-occurrence-id' }) selectedOccurrenceId = '';
  @property({ type: Number, attribute: 'ticket-total-cents' }) ticketTotalCents = 0;
  @property({ type: Number, attribute: 'ticket-unit-cents' }) ticketUnitCents = 0;
  /** r93 X1 — the SERVER's quote lines for a tiered basket; label only. */
  @property({ attribute: false }) ticketQuoteLines: TicketTotalLine[] = [];
  /** Per-kid cover price when it differs from the adult rate; 0 = same as adults. */
  @property({ type: Number, attribute: 'ticket-kids-unit-cents' }) ticketKidsUnitCents = 0;
  @property({ type: String, attribute: 'ticket-currency' }) ticketCurrency = '';
  @property({ type: Number, attribute: 'party-size' }) partySize = 0;
  @property({ type: Number, attribute: 'kids-count' }) kidsCount = 0;
  @property({ type: String, attribute: 'ticket-error' }) ticketError = '';
  // #953 — the promo code for a recurring occurrence. A series is only priced
  // once an occurrence is picked (here), so this is the first point a series
  // guest can apply a code. Same contract as step-service: everything comes
  // DOWN from the host (which owns the quote) and goes UP as `coupon-apply` /
  // `coupon-clear` from <yebo-ticket-coupon>; nothing here computes money.
  /** Server-computed pre-discount ticket total (cents); 0 = no coupon. */
  @property({ type: Number, attribute: 'ticket-subtotal-cents' }) ticketSubtotalCents = 0;
  /** Server-computed discount (cents); 0 = no coupon. */
  @property({ type: Number, attribute: 'ticket-discount-cents' }) ticketDiscountCents = 0;
  @property({ type: String, attribute: 'coupon-code' }) couponCode = '';
  /** The code the server ACCEPTED ('' when none). */
  @property({ type: String, attribute: 'coupon-applied-code' }) couponAppliedCode = '';
  /** A label only — never an input to arithmetic. */
  @property({ type: Number, attribute: 'coupon-percent-off' }) couponPercentOff = 0;
  /** −1 ⇒ the server sent no number ⇒ UNKNOWN. */
  @property({ type: Number, attribute: 'coupon-tickets-remaining' }) couponTicketsRemaining = -1;
  @property({ type: String, attribute: 'coupon-error' }) couponError = '';
  @property({ type: Boolean, attribute: 'coupon-applying' }) couponApplying = false;
  /** #954 — the server's label for an accepted TYPED code ("Buy 1 Get 1 Free"). */
  @property({ type: String, attribute: 'coupon-applied-label' }) couponAppliedLabel = '';
  // #954 — the series' AUTO deal for this sitting, as the last quote said it.
  // Passed straight to <yebo-ticket-coupon> (AC-954-37: same line / nudge as
  // the one-off service step); nothing here derives it.
  @property({ type: Boolean, attribute: 'deal-applied' }) dealApplied = false;
  @property({ type: String, attribute: 'deal-label' }) dealLabel = '';
  @property({ type: String, attribute: 'deal-reason' }) dealReason = '';
  @property({ type: String, attribute: 'deal-message' }) dealMessage = '';
  /**
   * How far ahead a guest may book, in days. #778.
   *
   * Was 60, which nothing ever set and no venue could change — so a default
   * chosen in passing (commit 8ec7afcc, one bullet in a twenty-line message,
   * no rationale recorded) hardened into a platform-wide ceiling. It was not
   * protecting anything: the availability API happily returns slots well
   * beyond it, and STAFF have never been capped at all, so the bookings were
   * not lost — venues were absorbing them as email enquiries and re-keying
   * them by hand.
   *
   * Creation lost a UK guest's 60th-birthday wine tour to it: she tried to
   * book 141 days out, the calendar's month arrows simply stopped, and she
   * emailed to ask what was wrong. A year is the floor for a venue selling to
   * travellers who plan a season ahead.
   *
   * Still an attribute, so an embed can raise it further
   * (`<yebo-booking max-advance-days="540">`) for a venue taking weddings.
   */
  @property({ type: Number, attribute: 'max-advance-days' }) maxAdvanceDays = 365;
  @property({ type: Array }) schedules: WorkingHoursSchedule[] = [];

  // R10.4 — empty-state recovery (all gated behind `recoveryEnabled`, the V2
  // flag). When OFF the empty state stays the original bare string so the fast
  // path is unchanged.
  @property({ type: Boolean, attribute: 'recovery-enabled' }) recoveryEnabled = false;
  @property({ type: String, attribute: 'availability-reason' }) availabilityReason = '';
  /**
   * The VENUE's published number (distinct from `phone`, which is the GUEST's, used
   * by the recovery notify form). Only surfaced on a dead-end empty state — see
   * `_emptyStateCta`. Empty when the venue has published none, in which case no
   * call-to-action is shown at all.
   */
  @property({ type: String, attribute: 'venue-phone' }) venuePhone = '';
  /** #592 — the email half of the dead-end way-forward. Same contract as
   *  venuePhone: absent renders nothing, never an empty "email us at ." */
  @property({ type: String, attribute: 'venue-email' }) venueEmail = '';
  @property({ type: Array }) alternateTimes: AvailabilityAlternateTime[] = [];
  @property({ type: Array }) alternateSections: AvailabilityAlternateSection[] = [];
  @property({ type: String, attribute: 'next-available-date' }) nextAvailableDate = '';
  @property({ attribute: false }) siblingVenue: { name: string; url: string } | null = null;
  @property({ type: String, attribute: 'first-name' }) firstName = '';
  @property({ type: String, attribute: 'last-name' }) lastName = '';
  @property({ type: String }) email = '';
  @property({ type: String }) phone = '';
  @property({ type: Boolean, attribute: 'notify-submitting' }) notifySubmitting = false;
  @property({ type: Boolean, attribute: 'notify-submitted' }) notifySubmitted = false;
  @property({ type: String, attribute: 'notify-error' }) notifyError = '';

  @state() private _viewYear = new Date().getFullYear();
  @state() private _viewMonth = new Date().getMonth();

  private get _todayStr(): string {
    return todayInTimezone(this.timezone);
  }

  /**
   * True when the grid has at least one slot the guest can actually pick. A grid
   * of only `available: false` slots is treated as empty so the recovery block
   * (R10.4) takes over rather than showing a wall of greyed-out buttons.
   */
  private get _hasBookableSlot(): boolean {
    return this.slots.some((s) => s.available);
  }

  /**
   * The one-line empty state shown when `recoveryEnabled` is OFF — the hosted
   * `book.html` path, i.e. most real guests.
   *
   * #521 follow-up: this used to be a hardcoded "No times available for this
   * date.", which blamed the DATE even when the server had said the real reason
   * was the PARTY SIZE. It now consults the shared copy source, falling back to
   * the original sentence whenever there is no reason (or an unrecognised one),
   * so a closed day still reads exactly as it always did.
   */
  private _emptyStateText(): string {
    return (
      availabilityReasonHeadline(this.availabilityReason, this.partySize) ??
      NO_AVAILABILITY_DEFAULT
    );
  }

  /**
   * The way forward offered alongside the empty state, or null when there isn't one.
   *
   * Only for a DEAD END (`no_table_for_party`): every other reason is date-specific,
   * and the calendar sitting right above is already the correct next move — adding a
   * "call us" there would push work onto the venue's phone for something the guest
   * can solve themselves. A dead end is different: no date helps, so without this the
   * guest's only options are to guess at dates that cannot work, or leave.
   *
   * Requires a published venue number; absent one there is nothing honest to offer.
   * `tel:` keeps only characters a dialler accepts, while the guest still SEES the
   * number formatted the way the venue wrote it.
   */
  /**
   * The ungated empty state: the outcome sentence, plus a way forward when the reason
   * is one no other date can fix. `role="status"` makes the swap audible rather than
   * silent — without it a screen-reader guest gets nothing where a sighted guest sees
   * the grid replaced.
   */
  /**
   * True when the ungated one-line empty state is the thing on screen. Includes the
   * `selectedDate` guard the surrounding block used to provide, since the live region
   * that renders this now lives outside that block.
   */
  // ── #529: don't drop keyboard focus when the slot grid unmounts ───────────
  //
  // A guest inside the time-slot listbox loses their place when availability
  // re-resolves to nothing: the grid unmounts, the focused button goes with it,
  // and focus reverts to <body>. Tab then restarts from the top of the document.
  //
  // The naive fix — focus the empty state whenever slots disappear — is worse
  // than the bug for anyone who had already tabbed to Back or out of the widget
  // entirely: it yanks them backwards. So the rescue is conditional on focus
  // having actually been inside the subtree that was removed, captured BEFORE
  // the render that removes it.
  private _hadFocusInSlots = false;

  /**
   * Is keyboard focus currently inside the slot grid?
   *
   * `ShadowRoot.activeElement`, not `document.activeElement` — from outside,
   * the document reports the HOST element for anything focused within a shadow
   * root, which would read as "not in the grid" every time.
   *
   * Returns false rather than throwing wherever the DOM is absent or partial
   * (node, SSR, a component constructed but never attached). A focus nicety
   * must never be able to break the booking step it lives in.
   */
  private _focusWasInSlots(): boolean {
    try {
      const root = this.renderRoot as ShadowRoot | null | undefined;
      const active = root?.activeElement as Element | null | undefined;
      if (!active || typeof active.closest !== 'function') return false;
      return !!active.closest('.slots-grid');
    } catch {
      return false;
    }
  }

  /**
   * The decision, kept pure so it can be tested without a DOM (this monorepo's
   * jsdom is unusable — see availability-empty-state.test.ts).
   *
   * Rescue only when focus WAS in the grid and the grid is now gone. Both halves
   * matter: the first stops us stealing focus from someone who moved on, the
   * second stops us firing on every unrelated re-render.
   */
  private _shouldRescueFocus(focusWasInSlots: boolean, hasBookableSlot: boolean): boolean {
    return focusWasInSlots && !hasBookableSlot;
  }

  protected willUpdate(changed?: Map<PropertyKey, unknown>): void {
    // Captured before the render that may remove the grid — afterwards the
    // focused node no longer exists and the question cannot be asked.
    this._hadFocusInSlots = this._focusWasInSlots();

    // #947 — open the calendar ON a preset date (an event's date) rather than
    // today's month. Only when the date actually changed, so manual month
    // browsing is never yanked back.
    if (changed?.has('selectedDate')) {
      const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(this.selectedDate || '');
      if (m) {
        this._viewYear = parseInt(m[1], 10);
        this._viewMonth = parseInt(m[2], 10) - 1;
      }
    }
  }

  protected updated(): void {
    if (!this._shouldRescueFocus(this._hadFocusInSlots, this._hasBookableSlot)) return;
    this._hadFocusInSlots = false;
    try {
      // The live region is in the DOM from this step's first paint and holds the
      // explanation the guest now needs, so it is the honest landing place.
      const target = (this.renderRoot as ShadowRoot | null)
        ?.querySelector('.empty-live') as HTMLElement | null;
      target?.focus?.();
    } catch {
      // Never let a focus move break the step.
    }
  }

  private get _showEmptyState(): boolean {
    return !!this.selectedDate && !this.loading && !this._hasBookableSlot && !this.recoveryEnabled;
  }

  /**
   * The CONTENT of the empty state only — the live region that wraps it is rendered
   * unconditionally by the caller, so that this text arrives as a mutation inside an
   * existing region rather than as a region that appears already-populated (which
   * screen readers generally do not announce).
   */
  private _renderEmptyStateContent() {
    const hint = this._emptyStateHint();
    const cta = this._emptyStateCta();
    const email = String(this.venueEmail ?? '').trim();
    return html`
      <p class="empty-state">${this._emptyStateText()}</p>
      ${hint ? html`<p class="empty-state-hint">${hint}</p>` : nothing}
      ${cta
        ? html`
          <a class="empty-state-cta" href=${cta.href}>
            ${cta.label} <span class="num">${cta.display}</span>
          </a>
        `
        : nothing}
      ${email && availabilityReasonIsDeadEnd(this.availabilityReason)
        ? html`
          <a class="empty-state-cta" href="mailto:${email}">
            Email us <span class="num">${email}</span>
          </a>
        `
        : nothing}
    `;
  }

  /**
   * What the guest can actually DO about a dead end.
   *
   * The phone CTA below is the richer answer and is now LIVE: #592 added
   * `contactPhone`/`contactEmail` to LocationConfig and the public /config
   * projection, and the widget root feeds them here (a host `phone-number`
   * attribute still wins). Before that they had nowhere to come from, which is
   * why this hint had to carry the whole burden.
   * The party size is the one lever the guest controls, and the Back button already
   * returns to that step — this names it so the route is stated rather than implied.
   */
  private _emptyStateHint(): string | null {
    if (!availabilityReasonIsDeadEnd(this.availabilityReason)) return null;
    return 'Go back to change the party size, or get in touch with the restaurant.';
  }

  private _emptyStateCta(): { href: string; label: string; display: string } | null {
    if (!availabilityReasonIsDeadEnd(this.availabilityReason)) return null;
    // String(...) not `?? ''`: the ATTRIBUTE path is always a string, but a host that
    // sets the PROPERTY directly could hand us a number or object, and .trim() would
    // throw inside render — taking the whole step down over a phone number.
    const display = String(this.venuePhone ?? '').trim();
    if (!display) return null;
    const dialable = display.replace(/[^+0-9]/g, '');
    if (!dialable) return null;
    return {
      href: `tel:${dialable}`,
      label: this.partySize > 0 ? `Call us to book for ${this.partySize}` : 'Call us to book',
      display,
    };
  }

  private get _maxDateStr(): string {
    const d = new Date();
    d.setDate(d.getDate() + this.maxAdvanceDays);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  }

  private get _canGoPrev(): boolean {
    const now = new Date();
    return this._viewYear > now.getFullYear() ||
      (this._viewYear === now.getFullYear() && this._viewMonth > now.getMonth());
  }

  private get _canGoNext(): boolean {
    const max = this._maxDateStr;
    const maxY = parseInt(max.slice(0, 4), 10);
    const maxM = parseInt(max.slice(5, 7), 10) - 1;
    return this._viewYear < maxY ||
      (this._viewYear === maxY && this._viewMonth < maxM);
  }

  private get _monthLabel(): string {
    const d = new Date(this._viewYear, this._viewMonth, 1);
    return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  /**
   * Check if a date is covered by any working hours schedule.
   * Returns true if dated schedules exist and none cover the date.
   *
   * Entries without a usable `startDate`/`endDate` (legacy `{isDefault,label,rules}`
   * shapes) carry no range to compare against, so they are ignored rather than
   * allowed to poison the list — a list of only undated entries blocks nothing,
   * exactly like an empty list.
   */
  private _isDateBlocked(dateStr: string): boolean {
    const dated = (this.schedules ?? []).filter(
      (s) => typeof s?.startDate === 'string' && s.startDate !== '' &&
        typeof s?.endDate === 'string' && s.endDate !== ''
    );
    if (dated.length === 0) return false;
    return !dated.some(s => dateStr >= s.startDate && dateStr <= s.endDate);
  }

  private get _calendarDays(): Array<{ date: string; day: number; disabled: boolean } | null> {
    const firstDay = new Date(this._viewYear, this._viewMonth, 1).getDay();
    const daysInMonth = new Date(this._viewYear, this._viewMonth + 1, 0).getDate();
    const today = this._todayStr;
    const maxDate = this._maxDateStr;

    const cells: Array<{ date: string; day: number; disabled: boolean } | null> = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);

    for (let d = 1; d <= daysInMonth; d++) {
      const mm = String(this._viewMonth + 1).padStart(2, '0');
      const dd = String(d).padStart(2, '0');
      const dateStr = `${this._viewYear}-${mm}-${dd}`;
      const outOfRange = dateStr < today || dateStr > maxDate;
      const noSchedule = this._isDateBlocked(dateStr);
      cells.push({ date: dateStr, day: d, disabled: outOfRange || noSchedule });
    }

    return cells;
  }

  private _navMonth(delta: number) {
    if (delta < 0 && !this._canGoPrev) return;
    if (delta > 0 && !this._canGoNext) return;
    let m = this._viewMonth + delta;
    let y = this._viewYear;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    this._viewMonth = m;
    this._viewYear = y;
  }

  private _selectDate(date: string) {
    this.dispatchEvent(new CustomEvent('date-selected', {
      detail: { date },
      bubbles: true,
      composed: true,
    }));
  }

  private _selectSlot(slot: AvailableSlot) {
    if (!slot.available) return;
    this.dispatchEvent(new CustomEvent('slot-selected', {
      detail: { start: slot.start, end: slot.end },
      bubbles: true,
      composed: true,
    }));
  }

  /** #130 — an occurrence the guest cannot book: sold out, or fewer covers left
   * than the party needs (mirrors step-service `_eventDisabled`'s party-fit check
   * so the guest sees the wall before clicking, not after the quote). */
  private _occurrenceDisabled(occ: { coversRemaining: number; soldOut?: boolean }): boolean {
    if (occ.soldOut) return true;
    if (this.partySize > 0 && occ.coversRemaining < this.partySize) return true;
    return false;
  }

  // #130 — guest picked a recurring occurrence. Emit the OPAQUE occId verbatim
  // (the host stores it + fires the quote); the date rides along for display.
  private _selectOccurrence(occ: { occId: string; date: string; coversRemaining: number; soldOut?: boolean }) {
    if (this._occurrenceDisabled(occ)) return;
    this.dispatchEvent(new CustomEvent('occurrence-selected', {
      detail: { occId: occ.occId, date: occ.date },
      bubbles: true,
      composed: true,
    }));
  }

  private _formatPrice(cents: number, currency: string): string {
    const major = cents / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: (currency || 'USD').toUpperCase(),
      }).format(major);
    } catch {
      return `${(currency || 'USD').toUpperCase()} ${major.toFixed(2)}`;
    }
  }

  /** "R300 pp × 4 guests" or, with kids priced separately, an adult+kid breakdown. */
  private _ticketTotalLabel() {
    // r93 X1 — a tiered basket has no single unit price: name the tiers.
    const tiered = tieredTicketTotalLabel(this.ticketQuoteLines);
    if (tiered) return tiered;
    const unitFormatted = this._formatPrice(this.ticketUnitCents, this.ticketCurrency);
    if (this.kidsCount > 0) {
      const adults = this.partySize - this.kidsCount;
      const kidsFormatted = this._formatPrice(this.ticketKidsUnitCents, this.ticketCurrency);
      return html`${unitFormatted} × ${adults} adult${adults !== 1 ? 's' : ''} + ${kidsFormatted} × ${this.kidsCount} kid${this.kidsCount !== 1 ? 's' : ''}`;
    }
    return html`${unitFormatted} pp × ${this.partySize} guest${this.partySize !== 1 ? 's' : ''}`;
  }

  /** #953 — a server-accepted 100% comp (see `isCompedQuote`). */
  private get _isComped(): boolean {
    return isCompedQuote(this.ticketTotalCents, this.ticketSubtotalCents, this.couponAppliedCode, this.dealApplied);
  }

  private _formatOccurrenceDate(date: string): string {
    const d = new Date(`${date}T12:00:00`);
    return Number.isNaN(d.getTime())
      ? date
      : d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
  }

  private _goBack() {
    this.dispatchEvent(new CustomEvent('step-back', { bubbles: true, composed: true }));
  }

  private _continue() {
    this.dispatchEvent(new CustomEvent('step-continue', { bubbles: true, composed: true }));
  }

  /** #953 — the promo block shows for a picked, priced, un-errored occurrence. */
  private get _showCouponBlock(): boolean {
    return !!this.selectedOccurrenceId
      && (this.ticketTotalCents > 0 || this.ticketSubtotalCents > 0)
      && !this.ticketError;
  }

  /**
   * #130 — recurring occurrence picker. Lists the series' bookable dates (with
   * remaining covers), shows the live quote total once a date is picked, and the
   * Continue button is gated on a chosen, in-stock occurrence with a valid quote.
   */
  private _renderOccurrencePicker() {
    return html`
      <div class="step-content">
        <h2 class="step-title">Choose a date</h2>
        ${this.occurrencesLoading
          ? html`<yebo-loading-spinner></yebo-loading-spinner>`
          : this.occurrences.length === 0
            ? html`<p class="occ-empty">No upcoming dates are available right now. Please check back soon.</p>`
            : html`
              <div class="occ-list" role="listbox" aria-label="Available dates">
                ${this.occurrences.map((occ) => {
                  const disabled = this._occurrenceDisabled(occ);
                  const selected = occ.occId === this.selectedOccurrenceId;
                  return html`
                    <button
                      type="button"
                      class="occ-option"
                      role="option"
                      aria-pressed=${selected ? 'true' : 'false'}
                      aria-selected=${selected}
                      aria-disabled=${disabled ? 'true' : 'false'}
                      ?disabled=${disabled}
                      @click=${() => this._selectOccurrence(occ)}
                    >
                      <span class="occ-date">${this._formatOccurrenceDate(occ.date)}</span>
                      ${occ.soldOut
                        ? html`<span class="occ-remaining soldout">Sold out</span>`
                        : occ.coversRemaining <= 10
                          ? html`<span class="occ-remaining">${occ.coversRemaining} left</span>`
                          : nothing}
                    </button>
                  `;
                })}
              </div>
            `}

        ${this.ticketError ? html`<p class="occ-error">${this.ticketError}</p>` : nothing}

        ${this._showCouponBlock ? html`
          <yebo-ticket-coupon
            external-status
            code=${this.couponCode}
            applied-code=${this.couponAppliedCode}
            percent-off=${this.couponPercentOff}
            subtotal-cents=${this.ticketSubtotalCents}
            discount-cents=${this.ticketDiscountCents}
            currency=${this.ticketCurrency}
            tickets-remaining=${this.couponTicketsRemaining}
            error=${this.couponError}
            ?applying=${this.couponApplying}
            applied-label=${this.couponAppliedLabel}
            ?deal-applied=${this.dealApplied}
            deal-label=${this.dealLabel}
            deal-reason=${this.dealReason}
            deal-message=${this.dealMessage}
          ></yebo-ticket-coupon>
        ` : nothing}

        <!--
          #954 a11y M1/M2 — the coupon's ONE live region, present from the
          picker's first paint (same reasoning as .empty-live below): the first
          deal line / nudge / refusal arrives as a mutation of an existing
          region. The coupon is "external-status", so it says none of it.
        -->
        <div class="coupon-live" role="status" aria-live="polite">${this._showCouponBlock
          ? renderCouponStatusContent({
              appliedCode: this.couponAppliedCode,
              dealApplied: this.dealApplied,
              dealLabel: this.dealLabel,
              dealMessage: this.dealMessage,
              discountCents: this.ticketDiscountCents,
              currency: this.ticketCurrency,
              error: this.couponError,
            })
          : nothing}</div>

        ${this.selectedOccurrenceId && (this.ticketTotalCents > 0 || this._isComped) && !this.ticketError ? html`
          <div class="occ-total">
            <span class="occ-total-label">${this._ticketTotalLabel()}</span>
            <span class="occ-total-value">${this._isComped
              ? COMPED_TOTAL_COPY
              : this._formatPrice(this.ticketTotalCents, this.ticketCurrency)}</span>
          </div>
        ` : nothing}
      </div>

      <div class="actions">
        <button class="btn btn-secondary" @click=${this._goBack}>Back</button>
        <button
          class="btn btn-primary"
          ?disabled=${!this.selectedOccurrenceId || (this.ticketTotalCents <= 0 && !this._isComped) || !!this.ticketError}
          @click=${this._continue}
        >Continue to checkout</button>
      </div>
    `;
  }

  render() {
    // #130 — a recurring series swaps the calendar for the occurrence picker.
    if (this.recurring) return this._renderOccurrencePicker();

    const today = this._todayStr;

    return html`
      <div class="step-content">
        <h2 class="step-title">Date &amp; Time</h2>

        <!-- Calendar -->
        <div>
          <div class="cal-header">
            <button class="cal-nav" ?disabled=${!this._canGoPrev} @click=${() => this._navMonth(-1)} aria-label="Previous month">&lsaquo;</button>
            <span class="cal-title">${this._monthLabel}</span>
            <button class="cal-nav" ?disabled=${!this._canGoNext} @click=${() => this._navMonth(1)} aria-label="Next month">&rsaquo;</button>
          </div>
          <div class="cal-grid" role="grid" aria-label="Calendar">
            ${WEEKDAYS.map(d => html`<div class="cal-weekday">${d}</div>`)}
            ${this._calendarDays.map(cell => {
              if (!cell) return html`<div class="cal-day empty"></div>`;
              const selected = cell.date === this.selectedDate;
              const isToday = cell.date === today;
              return html`
                <button
                  class="cal-day ${selected ? 'selected' : ''} ${isToday ? 'today' : ''} ${cell.disabled ? 'disabled' : ''}"
                  ?disabled=${cell.disabled}
                  @click=${() => this._selectDate(cell.date)}
                  aria-label="${cell.date}${selected ? ', selected' : ''}"
                  aria-pressed=${selected}
                >${cell.day}</button>
              `;
            })}
          </div>
        </div>

        <!-- Time slots -->
        ${this.selectedDate ? html`
          <div>
            ${this.loading
              ? html`<yebo-loading-spinner></yebo-loading-spinner>`
              : this._hasBookableSlot
                ? html`
                  <p class="slots-label">Available times</p>
                  <div class="slots-grid" role="listbox" aria-label="Available time slots">
                    ${this.slots.map(slot => html`
                      <button
                        class="slot-btn ${slot.start === this.selectedSlot ? 'selected' : ''} ${!slot.available ? 'unavailable' : ''}"
                        ?disabled=${!slot.available}
                        @click=${() => this._selectSlot(slot)}
                        role="option"
                        aria-selected=${slot.start === this.selectedSlot}
                      >${formatTime(slot.start, this.timezone)}</button>
                    `)}
                  </div>
                  <p class="tz-label">Times shown in ${this.timezone}</p>
                `
                : this.recoveryEnabled
                  // R10.4 — rich demand-recovery block instead of a dead end.
                  ? html`
                    <yebo-step-datetime-recovery
                      timezone=${this.timezone}
                      selected-date=${this.selectedDate ?? ''}
                      availability-reason=${this.availabilityReason}
                      .alternateTimes=${this.alternateTimes}
                      .alternateSections=${this.alternateSections}
                      next-available-date=${this.nextAvailableDate}
                      .siblingVenue=${this.siblingVenue}
                      first-name=${this.firstName}
                      last-name=${this.lastName}
                      email=${this.email}
                      phone=${this.phone}
                      ?notify-submitting=${this.notifySubmitting}
                      ?notify-submitted=${this.notifySubmitted}
                      notify-error=${this.notifyError}
                    ></yebo-step-datetime-recovery>
                  `
                  // Flag OFF — the fast path most hosted guests get. It stays a
                  // single line (no recovery UI), but #521's reason still reaches
                  // the guest instead of blaming the date.
                  : nothing
            }
          </div>
        ` : ''}

        <!--
          The live region sits OUTSIDE the selected-date block on purpose, so it is
          in the DOM from this step's first paint and only its CONTENT ever changes.
          Two earlier attempts got this wrong: role="status" on the empty-state node
          itself, then inside the date block — both produce a region that appears
          already-populated, which screen readers generally do NOT announce (they
          announce text mutating inside an EXISTING live node). Verified on staging:
          with the region inside the date block it was absent until the first date
          click and arrived with its text already in place, i.e. still silent.
        -->
        <div class="empty-live" role="status" aria-live="polite" tabindex="-1">
          ${this._showEmptyState ? this._renderEmptyStateContent() : nothing}
        </div>
      </div>

      <div class="actions">
        <button class="btn btn-secondary" @click=${this._goBack}>Back</button>
        <button
          class="btn btn-primary"
          ?disabled=${!this.selectedDate || !this.selectedSlot}
          @click=${this._continue}
        >Continue</button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'yebo-step-datetime': YeboStepDatetime;
  }
}
